// Small "Company Overhead" card shown above Quick access on the Top Level dashboard. Fed by
// GetOverheadPercentSummary (see spGetOverheadPercentSummary.sql): the overhead percentage the
// Consolidate Cash Flow report shows in its Overhead Summary panel, i.e.
// Non-Performing CC expenses / Performing CC expenses * 100, current FY vs previous FY,
// company-wide. A rising overhead % is unfavourable, so an increase reads red.
import React from 'react';
import { Loader2, Percent, ArrowUpRight, ArrowDownRight } from 'lucide-react';
import { formatCurrency } from '../../utilities/formatCurrency';

const pct = (v) => `${(parseFloat(v) || 0).toFixed(2)}%`;

const OverheadPercentCard = ({ loading, summary }) => {
    const current = parseFloat(summary?.OverheadPercentCurrentFY) || 0;
    const previous = parseFloat(summary?.OverheadPercentPreviousFY) || 0;
    const diff = parseFloat((current - previous).toFixed(2));
    const up = diff > 0;
    const hasPrevious = previous !== 0;

    return (
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 overflow-hidden transition-colors">
            <div className="flex items-center justify-between px-3 py-2 bg-amber-50 dark:bg-amber-900/20">
                <div className="flex items-center space-x-2 min-w-0">
                    <div className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 bg-amber-100 dark:bg-amber-900/30">
                        <Percent className="w-4 h-4 text-amber-600 dark:text-amber-500" />
                    </div>
                    <div className="min-w-0">
                        <p className="text-sm font-semibold text-gray-900 dark:text-white leading-tight">Company Overhead</p>
                        <p className="text-[11px] text-gray-500 dark:text-gray-400 leading-tight truncate">
                            {summary?.CurrentFY ? `FY ${summary.CurrentFY}` : 'Current FY'}
                        </p>
                    </div>
                </div>
                {loading ? (
                    <Loader2 className="w-4 h-4 animate-spin text-amber-600" />
                ) : (
                    <span className="text-base font-bold text-gray-900 dark:text-white whitespace-nowrap ml-2">
                        {summary ? pct(current) : '—'}
                    </span>
                )}
            </div>

            {!loading && summary && (
                <div className="px-3 py-2 text-xs space-y-1">
                    {hasPrevious && (
                        <div className="flex items-center justify-between">
                            <span className="text-gray-500 dark:text-gray-400">
                                vs {pct(previous)}{summary.PreviousFY ? ` in FY ${summary.PreviousFY}` : ' last FY'}
                            </span>
                            {diff !== 0 && (
                                <span className={`flex items-center font-semibold ${up ? 'text-red-600 dark:text-red-400' : 'text-green-600 dark:text-green-400'}`}>
                                    {up ? <ArrowUpRight className="w-3 h-3" /> : <ArrowDownRight className="w-3 h-3" />}
                                    {Math.abs(diff).toFixed(2)} pts
                                </span>
                            )}
                        </div>
                    )}
                    <div className="flex items-center justify-between text-[11px] text-gray-500 dark:text-gray-400">
                        <span>Non-Performing CC expenses</span>
                        <span className="tabular-nums text-gray-700 dark:text-gray-200">₹{formatCurrency(summary.NonPerformingExpenseCurrentFY || 0)}</span>
                    </div>
                    <div className="flex items-center justify-between text-[11px] text-gray-500 dark:text-gray-400">
                        <span>÷ Performing CC expenses</span>
                        <span className="tabular-nums text-gray-700 dark:text-gray-200">₹{formatCurrency(summary.PerformingExpenseCurrentFY || 0)}</span>
                    </div>
                </div>
            )}
            {!loading && !summary && (
                <p className="px-3 py-2 text-xs text-gray-400 dark:text-gray-500 text-center">No data available</p>
            )}
        </div>
    );
};

export default OverheadPercentCard;
